import React, { useState, useEffect, useContext } from "react";
import {
  CloseIconThin,
  HelpDrawer,
  HelpDrawerToggle,
} from "@cmsgov/ds-medicare-gov";
import { ScrollTable, CheckmarkIcon } from "../../../components";
import {
  comparisonTableRows,
  MedigapComparisonPlan,
  plans,
  ComparisonTableRowProps,
} from "./MedigapBenefitsHelpDrawer__Data";
import { useTranslate, sendTealiumEvent } from "../../../helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import MedigapWaiverStateLinks from "./MedigapWaiverStateLinks";
import { TealiumEventProps, sendTealiumView } from "../../../helpers/analytics";
import { AppStore } from "../../../app/store";

function RenderTableHeadings(t: (id: string) => string): React.ReactNode {
  return (
    <tr>
      <th scope="col" className="ds-u-fill--white ds-u-padding-left--0">
        {t("medigap.help_drawer.benefits")}
      </th>
      {plans.map((plan: MedigapComparisonPlan, i: number) => (
        <th key={i} scope="col">
          {plan.plan_name}
        </th>
      ))}
    </tr>
  );
}

export const RenderCells: React.FC<{
  plans: MedigapComparisonPlan[];
  benefitKey: string;
}> = ({ plans, benefitKey }) => {
  const t = useTranslate();

  return (
    <>
      {plans.map((plan: MedigapComparisonPlan, i: number) => {
        const benefitKeyValue: boolean | string = plan[benefitKey];

        return (
          <td key={i}>
            {typeof benefitKeyValue === "boolean" ? (
              <>
                <CheckmarkIcon
                  checked={benefitKeyValue}
                  classNames="MedigapTable__TableCheckmark"
                />
                <span className="ds-u-visibility--screen-reader">
                  {benefitKeyValue
                    ? t("medigap.help_drawer.plan_covers_100")
                    : t("medigap.help_drawer.plan_doesnt_cover")}
                </span>
              </>
            ) : (
              <>
                {benefitKeyValue}
                <span className="ds-u-visibility--screen-reader">
                  {t("medigap.help_drawer.amount_plan_covers")}
                </span>
              </>
            )}
          </td>
        );
      })}
    </>
  );
};

export const RenderComparisonRow: React.FC<ComparisonTableRowProps> = ({
  benefitKey,
  heading,
}) => (
  <tr key={benefitKey}>
    <td>{heading}</td>
    <RenderCells plans={plans} benefitKey={benefitKey} />
  </tr>
);

export const MedigapBenefitsHelpDrawer: React.FunctionComponent<{
  triggerLabel?: string;
  onOpenTealiumProps: TealiumEventProps;
}> = ({ triggerLabel, onOpenTealiumProps: { event_action, event_label } }) => {
  const { mctCurrentPlanYear: year } = useFlags();
  const { state } = useContext(AppStore.AppContext);
  const [open, setOpen] = useState(false);
  const t = useTranslate();

  const clickHandler = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (
      !target.closest(".mct-c-help-drawer") &&
      !target.matches(".help-drawer-toggle")
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("click", clickHandler);
      sendTealiumView(
        "Benefits offered by each Medigap plan",
        state,
        undefined,
        "help drawer"
      );
    } else {
      document.removeEventListener("click", clickHandler);
    }
  }, [open]);

  return (
    <>
      <HelpDrawerToggle
        helpDrawerOpen={open}
        showDrawer={(): void => {
          setOpen(true);
          sendTealiumEvent(event_action, event_label);
        }}
        className="help-drawer-toggle ds-u-text-align--left"
      >
        {triggerLabel ? triggerLabel : t("medigap.help_drawer.see_benefits")}
      </HelpDrawerToggle>

      {open && (
        <div className="mct-c-help-drawer ds-u-text-align--left MedigapBenefits__HelpDrawer">
          <HelpDrawer
            heading={t("medigap.help_drawer.heading")}
            onCloseClick={(): void => {
              setOpen(false);
              sendTealiumEvent(
                "Medigap - Find a Policy - Benefits offered by each Medigap plan",
                "Close"
              );
            }}
            closeButtonText={<CloseIconThin />}
          >
            <p>{t("medigap.help_drawer.compare_paragraph")}</p>

            <p className="MedigapTable__tableLegends" aria-hidden>
              <span className="ds-u-display--inline-block">
                <CheckmarkIcon checked={true} /> ={" "}
                {t("medigap.help_drawer.plan_covers_100")}
              </span>{" "}
              <span className="ds-u-display--inline-block">
                <CheckmarkIcon checked={false} /> ={" "}
                {t("medigap.help_drawer.plan_doesnt_cover")}
              </span>{" "}
              <span className="ds-u-display--inline-block">
                % = {t("medigap.help_drawer.amount_plan_covers")}
              </span>
            </p>

            <ScrollTable className="MedigapTable__Table">
              <thead>
                <tr>
                  <th colSpan={1} className="ds-u-fill--white"></th>
                  <th
                    colSpan={10}
                    className="ds-u-fill--white ds-u-text-align--left"
                  >
                    <h3>{t("medigap.help_drawer.medigap_plans")}</h3>
                  </th>
                </tr>
                {RenderTableHeadings(t)}
              </thead>

              <tbody>
                {comparisonTableRows.map((row, i: number) => {
                  const { benefitKey, heading } = row;
                  const finalHeading =
                    typeof heading === "string" ? t(heading) : heading;
                  return (
                    <RenderComparisonRow
                      key={i}
                      benefitKey={benefitKey}
                      heading={finalHeading}
                    />
                  );
                })}

                <tr className="MedigapTable__BottomRow">
                  <td
                    colSpan={8}
                    className="ds-u-fill--white ds-u-text-align--right"
                  >
                    $5,880
                  </td>
                  <td className="odd-man-out">$2,940</td>
                  <td colSpan={2} className="ds-u-fill--white"></td>
                </tr>

                <tr className="MedigapTable__BottomRow">
                  <td colSpan={7} className="ds-u-fill--white"></td>
                  <td colSpan={2}>
                    {t("medigap.help_drawer.out_of_pocket")}
                    <br />
                    {t("medigap.help_drawer.limit_in")} {year.toString()}
                  </td>
                  <td colSpan={2} className="ds-u-fill--white"></td>
                </tr>
              </tbody>
            </ScrollTable>

            <h4>{t("medigap.help_drawer.notes")}</h4>
            <ul className="ds-c-list mct-disc-list">
              <li>{t("medigap.help_drawer.plan_f_note")}</li>
              <li>{t("medigap.help_drawer.plan_k_note")}</li>
              <li>{t("medigap.help_drawer.plan_n_note")}</li>
            </ul>

            <MedigapWaiverStateLinks
              event_action="Medigap - Find a Policy - Benefits offered by each Medigap plan"
              event_label="Notes: "
            />
          </HelpDrawer>
        </div>
      )}
    </>
  );
};
